import { isConversationCountDrilldown, isAgentFeedbackDrilldownScore } from "./customReportLevel";
import React from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown, Download } from "lucide-react";
import MeasureDisplayModeControls from "./MeasureDisplayModeControls.jsx";
import {
  DISPLAY_VALUES,
  PERCENTAGE_OF_COLUMN,
  PERCENTAGE_OF_ROW,
  formatDisplayedMeasure,
  getAxisTotal,
  getDisplayModeSettings,
  getDisplayNumericValue,
  getGrandTotal,
  hasPercentageEligibleMeasure,
} from "./customReportValueDisplay.js";
import {
  buildColumnHeaderGroups,
  buildMultidimensionalPivotModel,
  getHierarchicalRowSpan,
  MAX_RENDERED_PIVOT_CELLS,
} from "./customReportVisualizationConfig.js";

const displayDimension = (value) =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const isMissingSortValue = (value) =>
  value === null || value === undefined || value === "" || value === "-";

const comparePivotValues = (left, right, direction = "asc") => {
  const leftMissing = isMissingSortValue(left);
  const rightMissing = isMissingSortValue(right);
  if (leftMissing && rightMissing) return 0;
  if (leftMissing) return 1;
  if (rightMissing) return -1;

  const leftNumber = Number(left);
  const rightNumber = Number(right);
  const comparison =
    Number.isFinite(leftNumber) && Number.isFinite(rightNumber)
      ? leftNumber - rightNumber
      : String(left).localeCompare(String(right), undefined, {
          numeric: true,
          sensitivity: "base",
        });
  return direction === "desc" ? -comparison : comparison;
};

export const escapePivotCsvValue = (value) => {
  const rawValue = value === null || value === undefined ? "" : String(value);
  let firstMeaningfulIndex = 0;
  while (
    firstMeaningfulIndex < rawValue.length &&
    rawValue.charCodeAt(firstMeaningfulIndex) <= 32
  ) {
    firstMeaningfulIndex += 1;
  }
  const firstMeaningfulCharacter = rawValue[firstMeaningfulIndex] || "";
  const stringValue =
    typeof value === "string" && ["=", "+", "-", "@"].includes(firstMeaningfulCharacter)
      ? `'${rawValue}`
      : rawValue;
  return /[",\r\n]/.test(stringValue) ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
};

const MultidimensionalPivotTable = ({
  visualization,
  reportLevel,
  tableModel,
  displaySettings,
  onDisplaySettingsChange,
  onCellClick,
}) => {
  const [sortConfig, setSortConfig] = React.useState(null);
  const { rows, dimensionColumns, measureColumns, getDisplayColumnLabel } = tableModel;
  const responseConfig = visualization?.data?.pivotConfig || visualization?.data?.pivot_config || {};
  const explicitRowKeys = Array.isArray(responseConfig.rowKeys) ? responseConfig.rowKeys : [];
  const explicitColumnKeys = Array.isArray(responseConfig.columnKeys) ? responseConfig.columnKeys : [];
  const logicalRows = Array.isArray(visualization?.pivotConfig?.rows) ? visualization.pivotConfig.rows : [];
  const logicalColumns = Array.isArray(visualization?.pivotConfig?.columns)
    ? visualization.pivotConfig.columns
    : [];
  const fallbackDimensionKeys = dimensionColumns.map((column) => column.key);
  const rowKeys = explicitRowKeys.length
    ? explicitRowKeys
    : fallbackDimensionKeys.slice(0, logicalRows.length);
  const columnKeys = explicitColumnKeys.length
    ? explicitColumnKeys
    : fallbackDimensionKeys.slice(logicalRows.length, logicalRows.length + logicalColumns.length);
  const dimensionByKey = new Map(dimensionColumns.map((column) => [String(column.key), column]));
  const responseBindings = [
    ...(Array.isArray(responseConfig.rowBindings) ? responseConfig.rowBindings : []),
    ...(Array.isArray(responseConfig.columnBindings) ? responseConfig.columnBindings : []),
  ];
  const drilldownBindingByResultKey = new Map(
    responseBindings
      .filter(
        (binding) =>
          String(binding?.resultKey || "").trim() !== "" &&
          String(binding?.module || "").trim() !== "" &&
          String(binding?.parameterKey || "").trim() !== ""
      )
      .map((binding) => [String(binding.resultKey), binding])
  );
  const missingResultKey = [...rowKeys, ...columnKeys].find((key) => !dimensionByKey.has(String(key)));

  if (
    responseConfig.truncated ||
    (Number(responseConfig.groupLimit) > 0 &&
      Number(responseConfig.groupCount) > Number(responseConfig.groupLimit)) ||
    responseConfig.renderedCellEstimate > MAX_RENDERED_PIVOT_CELLS
  ) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center px-6 text-center text-sm text-red-600">
        This pivot exceeds the {responseConfig.cellLimit || MAX_RENDERED_PIVOT_CELLS.toLocaleString()}-cell display limit. Reduce dimensions, values, or filters.
      </div>
    );
  }

  if (
    rowKeys.length < 1 ||
    columnKeys.length < 1 ||
    measureColumns.length < 1 ||
    rowKeys.length + columnKeys.length !== dimensionColumns.length ||
    missingResultKey
  ) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center px-6 text-center text-sm text-red-600">
        The pivot response does not match its configured row, column, and measure axes.
      </div>
    );
  }

  const model = buildMultidimensionalPivotModel({ rows, rowKeys, columnKeys, measureColumns });
  const renderedCellCount =
    model.rowTuples.length * model.columnTuples.length * measureColumns.length;
  if (renderedCellCount > MAX_RENDERED_PIVOT_CELLS) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center px-6 text-center text-sm text-red-600">
        This pivot would render {renderedCellCount.toLocaleString()} value cells. The limit is {MAX_RENDERED_PIVOT_CELLS.toLocaleString()}; reduce dimensions, values, or filters.
      </div>
    );
  }

  const rowColumns = rowKeys.map((key) => dimensionByKey.get(String(key)));
  const columnColumns = columnKeys.map((key) => dimensionByKey.get(String(key)));
  const hasPercentage = hasPercentageEligibleMeasure(measureColumns);
  const hasMultipleMeasures = measureColumns.length > 1;
  const singleConfiguredMeasure =
    Array.isArray(visualization?.measures) && visualization.measures.length === 1
      ? measureColumns[0]
      : null;
  const settings = getDisplayModeSettings(displaySettings);
  const displayMode = hasPercentage ? settings.mode : DISPLAY_VALUES;
  const percentageOf = settings.percentageOf;
  const showRowTotal = measureColumns.length === 1;
  const getRowTotal = (rowTuple, measureColumn) =>
    getAxisTotal(visualization?.data, "rows", rowTuple.values, measureColumn);
  const getColumnTotal = (columnTuple, measureColumn) =>
    getAxisTotal(visualization?.data, "columns", columnTuple.values, measureColumn);
  const getCellDenominator = (rowTuple, columnTuple, measureColumn) =>
    percentageOf === PERCENTAGE_OF_ROW
      ? getRowTotal(rowTuple, measureColumn)
      : getColumnTotal(columnTuple, measureColumn);
  const isSameSort = (left, right) =>
    left?.kind === right?.kind &&
    left?.rowIndex === right?.rowIndex &&
    left?.columnTupleKey === right?.columnTupleKey &&
    left?.measureKey === right?.measureKey;
  const cycleSort = (nextSort) => {
    setSortConfig((current) => {
      if (!isSameSort(current, nextSort)) return { ...nextSort, direction: "asc" };
      if (current.direction === "asc") return { ...nextSort, direction: "desc" };
      return null;
    });
  };
  const renderSortIcon = (candidate) => {
    if (!isSameSort(sortConfig, candidate)) {
      return <ChevronsUpDown size={13} className="text-gray-300" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp size={13} className="text-indigo-500" />
    ) : (
      <ArrowDown size={13} className="text-indigo-500" />
    );
  };
  const getAriaSort = (candidate) =>
    isSameSort(sortConfig, candidate)
      ? sortConfig.direction === "asc"
        ? "ascending"
        : "descending"
      : "none";
  const compareTupleValues = (left, right, startIndex = 0) => {
    for (let index = startIndex; index < rowKeys.length; index += 1) {
      const comparison = comparePivotValues(left?.values?.[index], right?.values?.[index]);
      if (comparison !== 0) return comparison;
    }
    return 0;
  };
  const compareParentPrefixes = (left, right) => {
    const parentDepth = Math.max(rowKeys.length - 1, 0);
    for (let index = 0; index < parentDepth; index += 1) {
      const comparison = comparePivotValues(left?.values?.[index], right?.values?.[index]);
      if (comparison !== 0) return comparison;
    }
    return 0;
  };
  const sortedRowTuples = [...model.rowTuples];
  if (sortConfig?.kind === "row-dimension") {
    sortedRowTuples.sort((left, right) => {
      for (let index = 0; index < sortConfig.rowIndex; index += 1) {
        const comparison = comparePivotValues(left?.values?.[index], right?.values?.[index]);
        if (comparison !== 0) return comparison;
      }
      const selectedComparison = comparePivotValues(
        left?.values?.[sortConfig.rowIndex],
        right?.values?.[sortConfig.rowIndex],
        sortConfig.direction
      );
      return selectedComparison !== 0
        ? selectedComparison
        : compareTupleValues(left, right, sortConfig.rowIndex + 1);
    });
  } else if (sortConfig?.kind === "cell-value") {
    const columnTuple = model.columnTuples.find(
      (item) => item.key === sortConfig.columnTupleKey
    );
    const measureColumn = measureColumns.find(
      (item) => String(item.key) === String(sortConfig.measureKey)
    );
    if (columnTuple && measureColumn) {
      sortedRowTuples.sort((left, right) => {
        const parentComparison = compareParentPrefixes(left, right);
        if (parentComparison !== 0) return parentComparison;
        const getSortValue = (rowTuple) => {
          const sourceRow = model.cellByTuple.get(
            `${rowTuple.key}\u0000${columnTuple.key}`
          );
          return getDisplayNumericValue({
            value: sourceRow?.[measureColumn.key],
            column: measureColumn,
            mode: displayMode,
            denominator: getCellDenominator(rowTuple, columnTuple, measureColumn),
          });
        };
        const valueComparison = comparePivotValues(
          getSortValue(left),
          getSortValue(right),
          sortConfig.direction
        );
        return valueComparison !== 0
          ? valueComparison
          : comparePivotValues(
              left?.values?.[rowKeys.length - 1],
              right?.values?.[rowKeys.length - 1]
            );
      });
    }
  } else if (sortConfig?.kind === "row-total" && showRowTotal) {
    const measureColumn = measureColumns[0];
    sortedRowTuples.sort((left, right) => {
      const parentComparison = compareParentPrefixes(left, right);
      if (parentComparison !== 0) return parentComparison;
      const getSortValue = (rowTuple) => {
        const value = getRowTotal(rowTuple, measureColumn);
        return getDisplayNumericValue({
          value,
          column: measureColumn,
          mode: displayMode,
          denominator:
            percentageOf === PERCENTAGE_OF_ROW
              ? value
              : getGrandTotal(visualization?.data, measureColumn),
        });
      };
      const valueComparison = comparePivotValues(
        getSortValue(left),
        getSortValue(right),
        sortConfig.direction
      );
      return valueComparison !== 0
        ? valueComparison
        : comparePivotValues(
            left?.values?.[rowKeys.length - 1],
            right?.values?.[rowKeys.length - 1]
          );
    });
  }
  const downloadPivot = () => {
    const headers = [
      ...rowColumns.map(getDisplayColumnLabel),
      ...model.columnTuples.flatMap((columnTuple) =>
        measureColumns.map((measureColumn) =>
          [
            ...columnTuple.values.map(displayDimension),
            getDisplayColumnLabel(measureColumn),
          ].join(" / ")
        )
      ),
    ];
    const csvRows = sortedRowTuples.map((rowTuple) => [
      ...rowTuple.values,
      ...model.columnTuples.flatMap((columnTuple) => {
        const sourceRow = model.cellByTuple.get(`${rowTuple.key}\u0000${columnTuple.key}`);
        return measureColumns.map((measureColumn) => sourceRow?.[measureColumn.key]);
      }),
    ]);
    const csv = [headers, ...csvRows]
      .map((row) => row.map(escapePivotCsvValue).join(","))
      .join("\n");
    const link = document.createElement("a");
    link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    link.download = `${visualization.title || "pivot"}_data.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="h-full min-h-0 overflow-auto px-3 pb-3" style={{ position: "relative" }}>
      <div className="sticky left-0 top-0 z-20 flex flex-wrap items-start justify-between gap-3 bg-white">
        <div>
          <div className="ml-3 mt-3 text-xl font-semibold">
            {visualization.title || `Visualization ${visualization.id}`}
          </div>
          <div className="mb-3 ml-3 text-xs text-gray-500">
            Rows: {rowColumns.map(getDisplayColumnLabel).join(" / ")} · Columns: {columnColumns.map(getDisplayColumnLabel).join(" / ")}
            {singleConfiguredMeasure
              ? ` · Measure: ${getDisplayColumnLabel(singleConfiguredMeasure)}`
              : ""}
          </div>
        </div>
        <div className="mr-3 mt-3 flex items-start gap-2">
          {hasPercentage ? (
            <MeasureDisplayModeControls
              mode={displayMode}
              percentageOf={percentageOf}
              allowRow
              onChange={onDisplaySettingsChange}
            />
          ) : null}
          <button
            type="button"
            aria-label={`Download ${visualization.title || "pivot"}`}
            onClick={downloadPivot}
            className="rounded p-1 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
          >
            <Download size={20} />
          </button>
        </div>
      </div>
      <table className="min-w-full border-separate border-spacing-0" data-testid="multidimensional-pivot-table">
        <thead>
          {columnKeys.map((key, level) => (
            <tr key={`${visualization.id}-column-level-${key}`} className="bg-gray-50">
              {level === 0
                ? rowColumns.map((rowColumn, rowIndex) => {
                    const rowSort = { kind: "row-dimension", rowIndex };
                    return (
                    <th
                      key={`${visualization.id}-row-axis-${rowColumn.key}`}
                      rowSpan={columnKeys.length + (hasMultipleMeasures ? 1 : 0)}
                      aria-sort={getAriaSort(rowSort)}
                      className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                    >
                      <button
                        type="button"
                        onClick={() => cycleSort(rowSort)}
                        className="inline-flex items-center gap-1 text-left"
                      >
                        {getDisplayColumnLabel(rowColumn)}
                        {renderSortIcon(rowSort)}
                      </button>
                    </th>
                    );
                  })
                : null}
              {buildColumnHeaderGroups(model.columnTuples, level, measureColumns.length).map((group) => {
                const columnTuple =
                  !hasMultipleMeasures && level === columnKeys.length - 1
                    ? model.columnTuples.find(
                        (item) => group.key === `${item.key}:${level}`
                      )
                    : null;
                const cellSort = columnTuple
                  ? {
                      kind: "cell-value",
                      columnTupleKey: columnTuple.key,
                      measureKey: measureColumns[0].key,
                    }
                  : null;
                return (
                  <th
                    key={`${visualization.id}-${group.key}`}
                    colSpan={group.colSpan}
                    aria-sort={cellSort ? getAriaSort(cellSort) : undefined}
                    className="border-b border-l border-gray-200 px-4 py-2 text-center text-xs font-semibold text-gray-600"
                  >
                    {cellSort ? (
                      <button
                        type="button"
                        onClick={() => cycleSort(cellSort)}
                        className="mx-auto inline-flex items-center gap-1"
                      >
                        {displayDimension(group.value)}
                        {renderSortIcon(cellSort)}
                      </button>
                    ) : (
                      displayDimension(group.value)
                    )}
                  </th>
                );
              })}
              {level === 0 && showRowTotal ? (
                <th
                  rowSpan={columnKeys.length + (hasMultipleMeasures ? 1 : 0)}
                  aria-sort={getAriaSort({ kind: "row-total", measureKey: measureColumns[0].key })}
                  className="border-b border-l border-gray-200 bg-gray-100 px-4 py-2 text-right text-xs font-semibold text-gray-600"
                >
                  <button
                    type="button"
                    onClick={() =>
                      cycleSort({ kind: "row-total", measureKey: measureColumns[0].key })
                    }
                    className="ml-auto inline-flex items-center gap-1"
                  >
                    Grand Total
                    {renderSortIcon({ kind: "row-total", measureKey: measureColumns[0].key })}
                  </button>
                </th>
              ) : null}
            </tr>
          ))}
          {hasMultipleMeasures ? (
            <tr className="bg-gray-50">
              {model.columnTuples.flatMap((columnTuple) =>
                measureColumns.map((measureColumn) => {
                  const cellSort = {
                    kind: "cell-value",
                    columnTupleKey: columnTuple.key,
                    measureKey: measureColumn.key,
                  };
                  return (
                    <th
                      key={`${visualization.id}-${columnTuple.key}-${measureColumn.key}`}
                      aria-sort={getAriaSort(cellSort)}
                      className="border-b border-l border-gray-200 px-4 py-2 text-right text-[11px] font-medium text-gray-500"
                    >
                      <button
                        type="button"
                        onClick={() => cycleSort(cellSort)}
                        className="ml-auto inline-flex items-center gap-1 text-right"
                      >
                        {getDisplayColumnLabel(measureColumn)}
                        {renderSortIcon(cellSort)}
                      </button>
                    </th>
                  );
                })
              )}
            </tr>
          ) : null}
        </thead>
        <tbody>
          {sortedRowTuples.length === 0 ? (
            <tr>
              <td
                colSpan={rowColumns.length + model.columnTuples.length * measureColumns.length + (showRowTotal ? 1 : 0)}
                className="px-4 py-3 text-sm text-gray-500"
              >
                No data available
              </td>
            </tr>
          ) : (
            <>
              {sortedRowTuples.map((rowTuple, rowIndex) => (
                <tr key={`${visualization.id}-row-${rowTuple.key}`} className="hover:bg-gray-50">
                  {rowTuple.values.map((value, level) => {
                    const span = getHierarchicalRowSpan(sortedRowTuples, rowIndex, level);
                    return span > 0 ? (
                      <th
                        key={`${visualization.id}-${rowTuple.key}-level-${level}`}
                        rowSpan={span}
                        className="border-b border-gray-100 bg-white px-4 py-3 text-left align-top text-sm font-medium text-gray-900"
                      >
                        {displayDimension(value)}
                      </th>
                    ) : null;
                  })}
                  {model.columnTuples.flatMap((columnTuple) => {
                    const sourceRow = model.cellByTuple.get(`${rowTuple.key}\u0000${columnTuple.key}`);
                    const axisValues = [...rowTuple.values, ...columnTuple.values];
                    const axisKeys = [...rowKeys, ...columnKeys];
                    const hasMappedLegacyAxes = axisValues.every(
                      (value) => value !== null && value !== undefined && value !== ""
                    ) && axisKeys.every((resultKey, index) => {
                      const binding = drilldownBindingByResultKey.get(String(resultKey));
                      return binding && (binding.module !== "agent_feedback" || isAgentFeedbackDrilldownScore(axisValues[index]));
                    });
                    const filters = axisKeys.map((resultKey, index) => {
                      const binding = drilldownBindingByResultKey.get(String(resultKey));
                      return {
                        module: binding?.module,
                        key: binding?.parameterKey,
                        value: axisValues[index],
                      };
                    });
                    return measureColumns.map((measureColumn) => {
                      const rawValue = sourceRow?.[measureColumn.key];
                      const isExactCount =
                        measureColumn?.drilldownMode === "exact_count" &&
                        Boolean(visualization?.drilldown?.context);
                      const isLegacyDrillable =
                        (!Object.prototype.hasOwnProperty.call(
                          measureColumn || {},
                          "drilldownMode"
                        ) || isConversationCountDrilldown(visualization, reportLevel)) && hasMappedLegacyAxes;
                      const isDrillable =
                        Number(rawValue) > 0 && (isExactCount || isLegacyDrillable);
                      return (
                        <td
                          key={`${visualization.id}-${rowTuple.key}-${columnTuple.key}-${measureColumn.key}`}
                          title={
                            isDrillable
                              ? (reportLevel === "calls" ? "View matching conversations" : "View matching customers")
                              : (reportLevel === "calls" ? "Conversation drill-down is unavailable for this value" : "Exact customer drill-down is unavailable for this value")
                          }
                          className={`border-b border-l border-gray-100 px-4 py-3 text-right text-sm tabular-nums ${
                            isDrillable
                              ? "cursor-pointer text-indigo-600 underline hover:text-indigo-700"
                              : "text-gray-500"
                          }`}
                          onClick={
                            isDrillable
                              ? () =>
                                  onCellClick?.(
                                    isExactCount
                                      ? {
                                          exactCount: true,
                                          measureId:
                                            measureColumn?.measure_id ??
                                            measureColumn?.measureId ??
                                            measureColumn?.key,
                                          dimensions: axisKeys.map((key, index) => ({
                                            key,
                                            label: getDisplayColumnLabel(
                                              dimensionByKey.get(String(key))
                                            ),
                                            value: axisValues[index],
                                          })),
                                        }
                                      : { filters }
                                  )
                              : undefined
                          }
                        >
                          {formatDisplayedMeasure({
                            value: rawValue,
                            column: measureColumn,
                            mode: displayMode,
                            denominator: getCellDenominator(
                              rowTuple,
                              columnTuple,
                              measureColumn
                            ),
                          })}
                        </td>
                      );
                    });
                  })}
                  {showRowTotal ? (
                    <td className="border-b border-l border-gray-100 bg-gray-50 px-4 py-3 text-right text-sm font-semibold tabular-nums text-gray-900">
                      {formatDisplayedMeasure({
                        value: getRowTotal(rowTuple, measureColumns[0]),
                        column: measureColumns[0],
                        mode: displayMode,
                        denominator:
                          percentageOf === PERCENTAGE_OF_ROW
                            ? getRowTotal(rowTuple, measureColumns[0])
                            : getGrandTotal(visualization?.data, measureColumns[0]),
                      })}
                    </td>
                  ) : null}
                </tr>
              ))}
              <tr className="bg-gray-100 font-semibold">
                <th
                  colSpan={rowColumns.length}
                  className="sticky left-0 bg-gray-100 px-4 py-3 text-left text-sm text-gray-900"
                >
                  Grand Total
                </th>
                {model.columnTuples.flatMap((columnTuple) =>
                  measureColumns.map((measureColumn) => {
                    const columnTotal = getColumnTotal(columnTuple, measureColumn);
                    return (
                      <td
                        key={`${visualization.id}-total-${columnTuple.key}-${measureColumn.key}`}
                        className="px-4 py-3 text-right text-sm tabular-nums text-gray-900"
                      >
                        {formatDisplayedMeasure({
                          value: columnTotal,
                          column: measureColumn,
                          mode: displayMode,
                          denominator:
                            percentageOf === PERCENTAGE_OF_COLUMN
                              ? columnTotal
                              : getGrandTotal(visualization?.data, measureColumn),
                        })}
                      </td>
                    );
                  })
                )}
                {showRowTotal ? (
                  <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-900">
                    {formatDisplayedMeasure({
                      value: getGrandTotal(visualization?.data, measureColumns[0]),
                      column: measureColumns[0],
                      mode: displayMode,
                      denominator: getGrandTotal(visualization?.data, measureColumns[0]),
                    })}
                  </td>
                ) : null}
              </tr>
            </>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default MultidimensionalPivotTable;
